// Client-side aggregation for the Reporting page — day/week/month/year bucketing, percentile
// series, and the SLA what-if simulator. Mirrors the standalone report's render.js logic.
import { median, p70, p90, mean } from '@/lib/businessDays';
import type { Granularity, Priority, RawTicket, WeeklyRow } from './types';

export function bucketKey(dateStr: string, granularity: Granularity): string {
  const d = new Date(dateStr);
  if (granularity === 'day') return d.toISOString().slice(0, 10);
  if (granularity === 'year') return `${d.getUTCFullYear()}-01-01`;
  if (granularity === 'month') return `${d.toISOString().slice(0, 7)}-01`;
  const day = d.getUTCDay();
  const diff = (day === 0 ? -6 : 1) - day;
  const monday = new Date(d);
  monday.setUTCDate(d.getUTCDate() + diff);
  return monday.toISOString().slice(0, 10);
}

export function bucketLabel(key: string, granularity: Granularity): string {
  const d = new Date(key);
  if (granularity === 'year') return String(d.getUTCFullYear());
  if (granularity === 'month') return d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

export interface AggBucket {
  key: string;
  label: string;
  created_count: number;
  resolved_count: number;
  breached_count: number;
  open_count: number;
  in_progress_count: number;
  closed_count: number;
  reopened_count: number;
  avg_hours: number | null;
  open_backlog: number;
}

export function aggregateDaily(
  rows: WeeklyRow[],
  granularity: Granularity,
  periodStart: Date | null,
  periodEnd: Date | null,
): AggBucket[] {
  const byKey = new Map<
    string,
    {
      created_count: number;
      resolved_count: number;
      breached_count: number;
      open_count: number;
      in_progress_count: number;
      closed_count: number;
      reopened_count: number;
      hoursWeighted: number;
      hoursWeight: number;
      open_backlog: number;
      lastDay: string | null;
    }
  >();
  for (const r of rows) {
    const key = bucketKey(r.day, granularity);
    if (!byKey.has(key)) {
      byKey.set(key, {
        created_count: 0,
        resolved_count: 0,
        breached_count: 0,
        open_count: 0,
        in_progress_count: 0,
        closed_count: 0,
        reopened_count: 0,
        hoursWeighted: 0,
        hoursWeight: 0,
        open_backlog: 0,
        lastDay: null,
      });
    }
    const b = byKey.get(key)!;
    b.created_count += r.created_count;
    b.resolved_count += r.resolved_count;
    b.breached_count += r.breached_count;
    b.open_count += r.open_count;
    b.in_progress_count += r.in_progress_count;
    b.closed_count += r.closed_count;
    b.reopened_count += r.reopened_count;
    if (r.avg_hours != null) {
      b.hoursWeighted += r.avg_hours * r.resolved_count;
      b.hoursWeight += r.resolved_count;
    }
    if (b.lastDay == null || r.day >= b.lastDay) {
      b.open_backlog = r.open_backlog;
      b.lastDay = r.day;
    }
  }
  let buckets = Array.from(byKey.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, b]) => ({
      key,
      label: bucketLabel(key, granularity),
      created_count: b.created_count,
      resolved_count: b.resolved_count,
      breached_count: b.breached_count,
      open_count: b.open_count,
      in_progress_count: b.in_progress_count,
      closed_count: b.closed_count,
      reopened_count: b.reopened_count,
      avg_hours: b.hoursWeight ? b.hoursWeighted / b.hoursWeight : null,
      open_backlog: b.open_backlog,
    }));
  if (periodStart || periodEnd) {
    buckets = buckets.filter((b) => {
      const d = new Date(b.key);
      if (periodStart && d < periodStart) return false;
      if (periodEnd && d > periodEnd) return false;
      return true;
    });
  }
  return buckets;
}

type HoursBetweenFn = (startStr: string, endStr: string) => number;

export function resolvedHours(tickets: RawTicket[], hoursBetween: HoursBetweenFn): number[] {
  return tickets.filter((t) => t.closedAt).map((t) => hoursBetween(t.createdAt, t.closedAt!));
}

export function respondedHours(tickets: RawTicket[], hoursBetween: HoursBetweenFn): number[] {
  return tickets.filter((t) => t.firstResponseAt).map((t) => hoursBetween(t.createdAt, t.firstResponseAt!));
}

function hoursByBucketField(
  tickets: RawTicket[],
  bucketKeys: string[],
  granularity: Granularity,
  field: 'closedAt' | 'firstResponseAt',
  hoursBetween: HoursBetweenFn,
): number[][] {
  const byKey = new Map<string, number[]>();
  for (const t of tickets) {
    const dateStr = t[field];
    if (!dateStr) continue;
    const key = bucketKey(dateStr, granularity);
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key)!.push(hoursBetween(t.createdAt, dateStr));
  }
  return bucketKeys.map((k) => byKey.get(k) || []);
}

export function avgByBucket(
  tickets: RawTicket[],
  bucketKeys: string[],
  granularity: Granularity,
  field: 'closedAt' | 'firstResponseAt',
  hoursBetween: HoursBetweenFn,
): (number | null)[] {
  return hoursByBucketField(tickets, bucketKeys, granularity, field, hoursBetween).map((hrs) => mean(hrs));
}

export function medianByBucket(
  tickets: RawTicket[],
  bucketKeys: string[],
  granularity: Granularity,
  field: 'closedAt' | 'firstResponseAt',
  hoursBetween: HoursBetweenFn,
): (number | null)[] {
  return hoursByBucketField(tickets, bucketKeys, granularity, field, hoursBetween).map((hrs) => median(hrs));
}

export function p70ByBucket(
  tickets: RawTicket[],
  bucketKeys: string[],
  granularity: Granularity,
  field: 'closedAt' | 'firstResponseAt',
  hoursBetween: HoursBetweenFn,
): (number | null)[] {
  return hoursByBucketField(tickets, bucketKeys, granularity, field, hoursBetween).map((hrs) => p70(hrs));
}

export function p90ByBucket(
  tickets: RawTicket[],
  bucketKeys: string[],
  granularity: Granularity,
  field: 'closedAt' | 'firstResponseAt',
  hoursBetween: HoursBetweenFn,
): (number | null)[] {
  return hoursByBucketField(tickets, bucketKeys, granularity, field, hoursBetween).map((hrs) => p90(hrs));
}

export interface SlaTargets {
  resolution: Partial<Record<Priority, number>>;
  response: Partial<Record<Priority, number>>;
}

export interface SlaStats {
  resolution: { resolved: number; breached: number; openPastDeadline: number };
  response: { total: number; met: number; missed: number; openPastDeadline: number };
}

// Per-priority count of still-open tickets whose age already exceeds the given per-priority
// SLA target (resolution or response deadline) — the priority-level breakdown of the overview
// "Open past SLA deadline" tile.
export function openPastDeadlineByPriority(
  tickets: RawTicket[],
  targets: Partial<Record<Priority, number>>,
  hoursBetween: HoursBetweenFn,
  now: Date,
  kind: 'resolution' | 'response',
): Record<Priority, number> {
  const counts = Object.fromEntries(PRIORITIES_ORDER.map((p) => [p, 0])) as Record<Priority, number>;
  for (const t of tickets) {
    if (t.closedAt) continue;
    if (kind === 'response' && t.firstResponseAt) continue;
    const target = targets[t.priority];
    if (target == null) continue;
    const ageH = hoursBetween(t.createdAt, now.toISOString());
    if (ageH > target) counts[t.priority]++;
  }
  return counts;
}
const PRIORITIES_ORDER: Priority[] = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];

export function computeSlaStats(
  tickets: RawTicket[],
  targets: SlaTargets,
  hoursBetween: HoursBetweenFn,
  now: Date,
): SlaStats {
  let resResolved = 0;
  let resBreached = 0;
  let resOpenPastDeadline = 0;
  let respTotal = 0;
  let respMet = 0;
  let respMissed = 0;
  let respOpenPastDeadline = 0;

  for (const t of tickets) {
    const resTarget = targets.resolution[t.priority];
    const respTarget = targets.response[t.priority];

    if (resTarget != null) {
      if (t.closedAt) {
        resResolved++;
        if (hoursBetween(t.createdAt, t.closedAt) > resTarget) resBreached++;
      } else {
        const ageH = hoursBetween(t.createdAt, now.toISOString());
        if (ageH > resTarget) resOpenPastDeadline++;
      }
    }

    if (respTarget != null) {
      respTotal++;
      if (t.firstResponseAt) {
        if (hoursBetween(t.createdAt, t.firstResponseAt) <= respTarget) respMet++;
        else respMissed++;
      } else if (t.closedAt) {
        respMissed++; // closed without ever logging a Unifiber response
      } else {
        const ageH = hoursBetween(t.createdAt, now.toISOString());
        if (ageH > respTarget) {
          respMissed++;
          respOpenPastDeadline++;
        }
      }
    }
  }

  return {
    resolution: { resolved: resResolved, breached: resBreached, openPastDeadline: resOpenPastDeadline },
    response: { total: respTotal, met: respMet, missed: respMissed, openPastDeadline: respOpenPastDeadline },
  };
}

export function pct(n: number, total: number): string {
  if (!total) return '0%';
  return `${((n / total) * 100).toFixed(1)}%`;
}

export function fmtHours(h: number | null): string {
  if (h == null) return '—';
  if (h >= 48) return `${(h / 24).toFixed(1)}d`;
  return `${h.toFixed(1)}h`;
}

export function fmtDays(d: number | null): string {
  if (d == null) return '—';
  return `${d.toFixed(2)}d`;
}
